import { useEffect, useState } from 'react';
import { Stack, ThemeProvider, useTheme } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import './NumberSelector.scss';
import { CacheProvider } from '@emotion/react';
import Button from '@mui/material/Button';
import { cacheLtr } from '../GeneralUtils';

type NumberSelectorProps = {
  value: number;
  setValue: (value: number) => void;
  min: number;
  max?: number;
  disabled?: boolean;
  isAddButtonDisabled?: boolean;
  isRemoveDisabled?: boolean;
};
const NumberSelector = ({
  value,
  setValue,
  min,
  max,
  disabled,
  isAddButtonDisabled,
  isRemoveDisabled,
}: NumberSelectorProps) => {
  const theme = useTheme();
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const clamp = (val: number) => {
    if (!!max && val > max) {
      return max;
    }
    return val < min ? min : val;
  };
  const changeVal = (val: number) => setValue(clamp(val));
  const commitDraft = () => {
    const parsed = parseInt(draft, 10);
    const clamped = clamp(Number.isNaN(parsed) ? min : parsed);
    setDraft(String(clamped));
    if (clamped !== value) {
      setValue(clamped);
    }
  };

  return (
    <CacheProvider value={cacheLtr}>
      <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
        <Stack direction="row" className="number-selector">
          <Button
            className="number-button"
            onClick={() => changeVal(value - 1)}
            variant="text"
            color="inherit"
            disabled={disabled || isRemoveDisabled}>
            <Remove />
          </Button>

          <input
            className="value-input"
            type="number"
            inputMode="numeric"
            value={draft}
            disabled={disabled}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={(event) => event.target.select()}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commitDraft();
                (event.target as HTMLInputElement).blur();
              }
            }}
          />

          <Button
            className="number-button"
            onClick={() => changeVal(value + 1)}
            variant="contained"
            color="primary"
            disabled={disabled || isAddButtonDisabled}>
            <Add sx={{ color: 'common.white' }} />
          </Button>
        </Stack>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default NumberSelector;
